import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, FileText, ArrowLeft, BarChart3, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { Modal, Field } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { today } from "@/lib/format";

const PERIODS = [
  { v: "today", l: "Today" }, { v: "yesterday", l: "Yesterday" },
  { v: "week", l: "This Week" }, { v: "month", l: "This Month" }, { v: "custom", l: "Custom" },
];
const isNum = (v) => typeof v === "number";

export default function ReportsCenter() {
  const { can } = useAuth();
  const [cards, setCards] = useState(null);
  const [error, setError] = useState("");
  const [active, setActive] = useState(null);
  const [period, setPeriod] = useState("month");
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [sendForm, setSendForm] = useState({ template_id: "", recipient_type: "owner", custom_number: "" });

  const openSend = async () => {
    try {
      const { data } = await api.get("/settings/whatsapp/templates");
      setTemplates(data.items);
      setSendForm({ template_id: data.items[0]?.template_id || "", recipient_type: "owner", custom_number: "" });
      setSendOpen(true);
    } catch (e) { toast.error(apiError(e)); }
  };
  const sendWhatsApp = async (e) => {
    e.preventDefault();
    if (!sendForm.template_id) { toast.error("Select a template."); return; }
    try {
      const { data } = await api.post("/whatsapp/send-report", {
        template_id: sendForm.template_id, recipient_type: sendForm.recipient_type,
        custom_number: sendForm.custom_number, ref_type: "report", ref_id: active.key,
        context: { report: active.title } });
      const st = data.log.status;
      toast[st === "sent" ? "success" : "info"](`WhatsApp ${st}${data.log.error ? `: ${data.log.error}` : ""}`);
      setSendOpen(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    api.get("/reports/catalog").then(({ data }) => setCards(data.items)).catch((e) => setError(apiError(e)));
  }, []);

  const params = useCallback(() => {
    if (period === "custom") {
      if (from && to && to < from) { toast.error("The 'To' date cannot be earlier than the 'From' date."); return null; }
      return { from_date: from || undefined, to_date: to || undefined };
    }
    return { period };
  }, [period, from, to]);

  const runReport = useCallback(async (key) => {
    const p = params(); if (p === null) return;
    setBusy(true); setReport(null);
    try { const { data } = await api.get(`/reports/data/${key}`, { params: p }); setReport(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  }, [params]);

  useEffect(() => { if (active) runReport(active.key); }, [active, runReport]);

  const exportFile = async (fmt) => {
    const p = params(); if (p === null) return;
    try {
      const res = await api.get(`/reports/export/${active.key}`, { params: { ...p, format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${active.key}_report.${fmt}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!can("hospital.reports.view") && !can("lab.reports.view") && !can("pharmacy.reports.view") && !can("finance.reports.view") && !can("hospital.earnings.view"))
    return <NoAccess what="Reports" />;

  if (active) {
    return (
      <div data-testid="report-view">
        <button onClick={() => { setActive(null); setReport(null); }} className={`${ghostBtnCls} mb-4`} data-testid="report-back"><ArrowLeft className="h-4 w-4" /> All reports</button>
        <PageHeader overline="Reports Center" title={active.title}
          subtitle="Filter by period or a custom date range, then export to PDF or CSV." />
        <div className="flex flex-wrap items-end gap-3 mb-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Period</p>
            <div className="flex flex-wrap gap-2">
              {PERIODS.map((p) => (
                <button key={p.v} onClick={() => setPeriod(p.v)} data-testid={`period-${p.v}`}
                  className={`rounded-full px-3.5 py-1.5 text-sm font-medium border ${period === p.v ? "bg-[#0F172A] text-white border-[#0F172A]" : "border-slate-300 text-slate-700 hover:border-slate-900"}`}>{p.l}</button>
              ))}
            </div>
          </div>
          {period === "custom" && (
            <>
              <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">From</span>
                <input type="date" className={`${inputCls} mt-1`} data-testid="report-from" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} /></label>
              <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">To</span>
                <input type="date" className={`${inputCls} mt-1`} data-testid="report-to" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} /></label>
              <button onClick={() => runReport(active.key)} className={ghostBtnCls} data-testid="report-apply">Apply</button>
            </>
          )}
          <div className="ml-auto flex gap-2">
            <button onClick={openSend} className={ghostBtnCls} data-testid="report-send-whatsapp"><Send className="h-4 w-4" /> WhatsApp</button>
            <button onClick={() => exportFile("csv")} className={ghostBtnCls} data-testid="export-csv"><FileDown className="h-4 w-4" /> CSV</button>
            <button onClick={() => exportFile("pdf")} className={primaryBtnCls} data-testid="export-pdf"><FileText className="h-4 w-4" /> PDF</button>
          </div>
        </div>

        <Modal open={sendOpen} onClose={() => setSendOpen(false)} title="Send report on WhatsApp"
          subtitle="Uses a configured Interakt template. Delivery is recorded in the WhatsApp log." testid="report-send-modal">
          <form onSubmit={sendWhatsApp} className="space-y-4">
            <Field label="Template">
              <select className={inputCls} data-testid="send-template" value={sendForm.template_id} onChange={(e) => setSendForm({ ...sendForm, template_id: e.target.value })}>
                {templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}{t.active ? "" : " (inactive)"}</option>)}
              </select>
            </Field>
            <Field label="Recipient">
              <select className={inputCls} data-testid="send-recipient" value={sendForm.recipient_type} onChange={(e) => setSendForm({ ...sendForm, recipient_type: e.target.value })}>
                {["owner", "admin", "finance", "lab", "custom"].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            {sendForm.recipient_type === "custom" && (
              <Field label="Custom number"><input className={inputCls} data-testid="send-custom-number" value={sendForm.custom_number} onChange={(e) => setSendForm({ ...sendForm, custom_number: e.target.value })} /></Field>
            )}
            <div className="flex justify-end"><button className={primaryBtnCls} data-testid="send-whatsapp-confirm">Send</button></div>
          </form>
        </Modal>

        {busy || !report ? <Loading /> : (
          <>
            {report.summary && Object.keys(report.summary).length > 0 && (
              <div className="flex flex-wrap gap-4 mb-5" data-testid="report-summary">
                {Object.entries(report.summary).map(([k, v]) => (
                  <div key={k} className="panel px-4 py-3"><p className="text-xs text-slate-400">{k}</p><p className="text-lg font-bold text-slate-900">{isNum(v) ? v.toLocaleString("en-IN") : v}</p></div>
                ))}
              </div>
            )}
            <Table testid="report-table" columns={report.columns}
              rows={report.rows} empty={<EmptyState title="No data for this period" testid="report-empty" />}
              renderRow={(r, i) => (
                <tr key={i} data-testid={`report-row-${i}`}>
                  {r.map((c, j) => <Td key={j} className="text-slate-700">{isNum(c) ? c.toLocaleString("en-IN") : (c || "—")}</Td>)}
                </tr>
              )} />
          </>
        )}
      </div>
    );
  }

  return (
    <div data-testid="reports-center-page">
      <PageHeader overline="Reports" title="Reports Center"
        subtitle="Every operational and financial report in one place — OPD, IPD, doctors, patients, billing, lab, pharmacy and expenses. Filter by date and export to PDF or CSV." />
      {error && <ErrorState message={error} />}
      {!cards ? <Loading /> : cards.length === 0 ? (
        <div className="panel"><EmptyState title="No reports available" subtitle="Your role does not include report access." testid="reports-empty" /></div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="report-cards">
          {cards.map((c) => (
            <button key={c.key} onClick={() => { setActive(c); }} data-testid={`report-card-${c.key}`}
              className="panel p-5 text-left transition hover:border-slate-900 hover:shadow-md">
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-slate-100 text-slate-600 mb-3"><BarChart3 className="h-5 w-5" /></div>
              <p className="font-ui text-base font-bold text-slate-900">{c.title}</p>
              <p className="text-xs text-slate-500 mt-1">View, filter and export</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
